import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { MapPin, Mail, Phone, MessageCircle, Map, Save, Building2, ImageIcon, Upload, X, Loader2, Star, Type, Briefcase, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanySettings = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const [formData, setFormData] = useState({
    company_name: '',
    logo_url: '',
    favicon_url: '',
    site_title: '',
    site_description: '',
    address_en: '',
    address_id: '',
    email: '',
    phone: '',
    whatsapp: '',
    google_maps_url: '',
    hr_email: '',
    hr_name: '',
    career_categories: []
  });
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/contact-settings`);
      setFormData({
        company_name: response.data.company_name || '',
        logo_url: response.data.logo_url || '',
        favicon_url: response.data.favicon_url || '',
        site_title: response.data.site_title || '',
        site_description: response.data.site_description || '',
        address_en: response.data.address_en || '',
        address_id: response.data.address_id || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        whatsapp: response.data.whatsapp || '',
        google_maps_url: response.data.google_maps_url || '',
        hr_email: response.data.hr_email || '',
        hr_name: response.data.hr_name || 'Human Resources',
        career_categories: response.data.career_categories && response.data.career_categories.length
          ? response.data.career_categories
          : ['Construction', 'IT & Digital', 'General']
      });
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to fetch settings' : 'Gagal mengambil pengaturan');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (type === 'favicon') {
      allowedTypes.push('image/x-icon', 'image/vnd.microsoft.icon');
    }
    
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'en' ? 'Invalid file type. Please upload an image.' : 'Tipe file tidak valid. Silakan unggah gambar.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'en' ? 'File too large. Maximum 5MB.' : 'File terlalu besar. Maksimal 5MB.');
      return;
    }

    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
    setUploading(true);

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Use the relative URL since uploads are in frontend public folder
      const fileUrl = response.data.url;
      
      if (type === 'logo') {
        setFormData(prev => ({ ...prev, logo_url: fileUrl }));
      } else {
        setFormData(prev => ({ ...prev, favicon_url: fileUrl }));
      }
      
      toast.success(language === 'en' ? 'File uploaded successfully!' : 'File berhasil diunggah!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Upload failed' : 'Gagal mengunggah'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`${API}/contact-settings`, formData);
      toast.success(language === 'en' ? 'Settings saved successfully!' : 'Pengaturan berhasil disimpan!');
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to save settings' : 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={language === 'en' ? 'Company Settings' : 'Pengaturan Perusahaan'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={language === 'en' ? 'Company Settings' : 'Pengaturan Perusahaan'}>
      <div className="max-w-3xl" data-testid="company-settings-page">
        <p className="text-stone-600 mb-8">
          {language === 'en' 
            ? 'Manage your company branding and contact information displayed on the website.'
            : 'Kelola branding perusahaan dan informasi kontak yang ditampilkan di website.'}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Company Branding */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                  {language === 'en' ? 'Company Branding' : 'Branding Perusahaan'}
                </CardTitle>
                <CardDescription>
                  {language === 'en' ? 'Company name and logo displayed across the website' : 'Nama dan logo perusahaan yang ditampilkan di seluruh website'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{language === 'en' ? 'Company Name' : 'Nama Perusahaan'}</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    placeholder="Citra Abi Permana"
                    className="mt-1"
                    data-testid="company-name-input"
                  />
                </div>
                
                {/* Logo Upload */}
                <div>
                  <Label className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    {language === 'en' ? 'Company Logo' : 'Logo Perusahaan'}
                  </Label>
                  <div className="mt-2 space-y-3">
                    <div className="flex gap-2">
                      <Input
                        value={formData.logo_url}
                        onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                        placeholder="https://example.com/logo.png"
                        className="flex-1"
                        data-testid="logo-url-input"
                      />
                      <input
                        type="file"
                        ref={logoInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'logo')}
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        data-testid="upload-logo-btn"
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-stone-500">
                      {language === 'en' 
                        ? 'Enter URL or upload an image (PNG, SVG, JPG - max 5MB)'
                        : 'Masukkan URL atau unggah gambar (PNG, SVG, JPG - maks 5MB)'}
                    </p>
                    {formData.logo_url && (
                      <div className="p-4 bg-stone-50 rounded-lg flex items-center gap-4">
                        <img 
                          src={formData.logo_url} 
                          alt="Logo Preview" 
                          className="h-16 w-auto object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setFormData({...formData, logo_url: ''})}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4 mr-1" />
                          {language === 'en' ? 'Remove' : 'Hapus'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Favicon Upload */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    {language === 'en' ? 'Website Favicon' : 'Favicon Website'}
                  </Label>
                  <div className="mt-2 space-y-3">
                    <div className="flex gap-2">
                      <Input
                        value={formData.favicon_url}
                        onChange={(e) => setFormData({...formData, favicon_url: e.target.value})}
                        placeholder="https://example.com/favicon.ico"
                        className="flex-1"
                        data-testid="favicon-url-input"
                      />
                      <input
                        type="file"
                        ref={faviconInputRef}
                        className="hidden"
                        accept="image/*,.ico"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'favicon')}
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => faviconInputRef.current?.click()}
                        disabled={uploadingFavicon}
                        data-testid="upload-favicon-btn"
                      >
                        {uploadingFavicon ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-stone-500">
                      {language === 'en' 
                        ? 'Small icon shown in browser tab (ICO, PNG - recommended 32x32px)'
                        : 'Ikon kecil yang ditampilkan di tab browser (ICO, PNG - disarankan 32x32px)'}
                    </p>
                    {formData.favicon_url && (
                      <div className="p-4 bg-stone-50 rounded-lg flex items-center gap-4">
                        <img 
                          src={formData.favicon_url} 
                          alt="Favicon Preview" 
                          className="h-8 w-8 object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setFormData({...formData, favicon_url: ''})}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4 mr-1" />
                          {language === 'en' ? 'Remove' : 'Hapus'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Site Title */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Type className="w-4 h-4" />
                    {language === 'en' ? 'Browser Tab Title' : 'Judul Tab Browser'}
                  </Label>
                  <Input
                    value={formData.site_title}
                    onChange={(e) => setFormData({...formData, site_title: e.target.value})}
                    placeholder={language === 'en' ? 'e.g., Citra Abi Permana | Technology & Construction' : 'contoh: Citra Abi Permana | Teknologi & Konstruksi'}
                    className="mt-2"
                    data-testid="site-title-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    {language === 'en' 
                      ? 'Text shown in browser tab and when sharing links (WhatsApp, Facebook, etc.)'
                      : 'Teks yang ditampilkan di tab browser dan saat berbagi tautan (WhatsApp, Facebook, dll.)'}
                  </p>
                </div>

                {/* Site Description */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Type className="w-4 h-4" />
                    {language === 'en' ? 'Site Description' : 'Deskripsi Situs'}
                  </Label>
                  <Textarea
                    value={formData.site_description}
                    onChange={(e) => setFormData({...formData, site_description: e.target.value})}
                    placeholder={language === 'en' ? 'e.g., Integrated Construction and Green Digital Solutions...' : 'contoh: Solusi Konstruksi dan Digital Hijau yang Terintegrasi...'}
                    rows={2}
                    className="mt-2"
                    data-testid="site-description-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    {language === 'en' 
                      ? 'Short description shown in link previews and search results (recommended: 150-160 characters)'
                      : 'Deskripsi singkat yang ditampilkan di pratinjau tautan dan hasil pencarian (disarankan: 150-160 karakter)'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                  {language === 'en' ? 'Address' : 'Alamat'}
                </CardTitle>
                <CardDescription>
                  {language === 'en' ? 'Company physical address' : 'Alamat fisik perusahaan'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{language === 'en' ? 'Address (English)' : 'Alamat (Inggris)'}</Label>
                  <Textarea
                    value={formData.address_en}
                    onChange={(e) => setFormData({...formData, address_en: e.target.value})}
                    placeholder="123 Main Street, Jakarta, Indonesia"
                    rows={2}
                    className="mt-1"
                    data-testid="address-en-input"
                  />
                </div>
                <div>
                  <Label>{language === 'en' ? 'Address (Indonesian)' : 'Alamat (Indonesia)'}</Label>
                  <Textarea
                    value={formData.address_id}
                    onChange={(e) => setFormData({...formData, address_id: e.target.value})}
                    placeholder="Jl. Utama No. 123, Jakarta, Indonesia"
                    rows={2}
                    className="mt-1"
                    data-testid="address-id-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Phone className="w-5 h-5 text-emerald-600" />
                  {language === 'en' ? 'Contact Information' : 'Informasi Kontak'}
                </CardTitle>
                <CardDescription>
                  {language === 'en' ? 'Phone, email, and messaging' : 'Telepon, email, dan pesan'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="info@company.com"
                      className="mt-1"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {language === 'en' ? 'Phone Number' : 'Nomor Telepon'}
                    </Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+62 21 1234 5678"
                      className="mt-1"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp ({language === 'en' ? 'Optional' : 'Opsional'})
                  </Label>
                  <Input
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                    placeholder="+62 812 3456 7890"
                    className="mt-1"
                    data-testid="whatsapp-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    {language === 'en' 
                      ? 'Include country code for WhatsApp click-to-chat'
                      : 'Sertakan kode negara untuk fitur klik-untuk-chat WhatsApp'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Google Maps */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Map className="w-5 h-5 text-emerald-600" />
                  Google Maps ({language === 'en' ? 'Optional' : 'Opsional'})
                </CardTitle>
                <CardDescription>
                  {language === 'en' ? 'Embed link for Google Maps' : 'Link embed untuk Google Maps'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div>
                  <Label>{language === 'en' ? 'Google Maps Embed URL' : 'URL Embed Google Maps'}</Label>
                  <Input
                    value={formData.google_maps_url}
                    onChange={(e) => setFormData({...formData, google_maps_url: e.target.value})}
                    placeholder="https://www.google.com/maps/embed?pb=..."
                    className="mt-1"
                    data-testid="maps-url-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    {language === 'en' 
                      ? 'Get the embed URL from Google Maps share option'
                      : 'Dapatkan URL embed dari opsi berbagi Google Maps'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Careers Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Briefcase className="w-5 h-5 text-emerald-600" />
                  {language === 'en' ? 'Careers Configuration' : 'Konfigurasi Karir'}
                </CardTitle>
                <CardDescription>
                  {language === 'en'
                    ? 'HR email and job categories for the careers page'
                    : 'Email HR dan kategori pekerjaan untuk halaman karir'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {language === 'en' ? 'HR Email Recipient' : 'Email Penerima HR'}
                    </Label>
                    <Input
                      type="email"
                      value={formData.hr_email}
                      onChange={(e) => setFormData({ ...formData, hr_email: e.target.value })}
                      placeholder="hr@company.com"
                      className="mt-1"
                      data-testid="hr-email-input"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      {language === 'en'
                        ? 'Job applications will be sent to this email address'
                        : 'Lamaran kerja akan dikirim ke alamat email ini'}
                    </p>
                  </div>
                  <div>
                    <Label>{language === 'en' ? 'HR Display Name' : 'Nama Tampil HR'}</Label>
                    <Input
                      value={formData.hr_name}
                      onChange={(e) => setFormData({ ...formData, hr_name: e.target.value })}
                      placeholder="Human Resources"
                      className="mt-1"
                      data-testid="hr-name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>{language === 'en' ? 'Job Categories' : 'Kategori Pekerjaan'}</Label>
                  <p className="text-xs text-stone-500 mt-1 mb-2">
                    {language === 'en'
                      ? 'Categories used to group positions on the careers page'
                      : 'Kategori untuk mengelompokkan posisi di halaman karir'}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2" data-testid="career-categories-list">
                    {formData.career_categories.map((cat, idx) => (
                      <div
                        key={`${cat}-${idx}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm border border-emerald-200"
                        data-testid={`category-chip-${idx}`}
                      >
                        <span>{cat}</span>
                        <button
                          type="button"
                          onClick={() => setFormData({
                            ...formData,
                            career_categories: formData.career_categories.filter((_, i) => i !== idx)
                          })}
                          className="hover:text-red-600 ml-1"
                          data-testid={`remove-category-${idx}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const trimmed = newCategory.trim();
                          if (trimmed && !formData.career_categories.includes(trimmed)) {
                            setFormData({
                              ...formData,
                              career_categories: [...formData.career_categories, trimmed]
                            });
                            setNewCategory('');
                          }
                        }
                      }}
                      placeholder={language === 'en' ? 'Add a new category...' : 'Tambah kategori baru...'}
                      className="flex-1"
                      data-testid="new-category-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const trimmed = newCategory.trim();
                        if (trimmed && !formData.career_categories.includes(trimmed)) {
                          setFormData({
                            ...formData,
                            career_categories: [...formData.career_categories, trimmed]
                          });
                          setNewCategory('');
                        }
                      }}
                      data-testid="add-category-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {language === 'en' ? 'Add' : 'Tambah'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    {language === 'en' ? 'Saving...' : 'Menyimpan...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    {language === 'en' ? 'Save Settings' : 'Simpan Pengaturan'}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default CompanySettings;
